import { EventListener, Event, EncoderDecoderInterface } from "silver";

interface StorageKeyUpdateEvent<T> extends Event {
	sender: StorageKeyObserver<T>;
	value: T|undefined;
}

export class StorageKeyObserver<T> {
	public readonly onChange = EventListener.create<StorageKeyUpdateEvent<T>>("update");
	private cachedValue?: T;
	public locked: boolean = false;
	constructor(
		private readonly storage: Storage,
		public readonly key: string,
		public readonly encoder: EncoderDecoderInterface<T>
	) {
		window.addEventListener("storage", (e) => {
			if (e.key == this.key) {
				this.setValue(this.decode(e.newValue), true);
			}
		});
	}

	public get value(): T|undefined {
		return this.decode(this.storage.getItem(this.key));
	}

	public set value(value: T|undefined) {
		if (this.locked) {
			return;
		}
		this.setValue(value, false);
	}

	private decode(value: string|null): T|undefined {
		if (value === null) {
			return undefined;
		}
		return this.encoder.decode(value);
	}

	private encode(value: T|undefined): string|null {
		if (value === undefined) {
			return null;
		}
		return this.encoder.encode(value);
	}

	private setValue(value: T|undefined, cacheOnly: boolean) {
		if (value === this.cachedValue) {
			return;
		}

		this.cachedValue = value;
		this.onChange.trigger({ sender: this, value });

		if (cacheOnly) {
			return;
		}

		const encoded: null|string = this.encode(value);
		if (encoded === null) {
			this.storage.removeItem(this.key);
		} else {
			this.storage.setItem(this.key, encoded);
		}

		try {
			window.dispatchEvent(new StorageEvent("storage", {
				key:      this.key,
				newValue: encoded
			}));
		} catch (er) {
			console.error(er);
		}
	}
}
